import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Row, Col, Button, DatePicker, Select, Alert, Tabs, Card, Statistic, Spin } from 'antd';
import { SearchOutlined, FilePdfOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import RevenuePrintView from '@/components/views/RevenuePrintView';
import dayjs from 'dayjs';
import PageContainer from '@/components/PageContainer';
import RevenueSummaryTab from '@/components/views/RevenueSummaryTab';
import RevenuePvTab from '@/components/views/RevenuePvTab';
import RevenueStorageTab from '@/components/views/RevenueStorageTab';
import CapacityFeeTab from '@/components/views/CapacityFeeTab';
import { usePvRevenue, useStorageRevenue } from '@/api/revenue';
import { useAgcStorage } from '@/api/agc';
import { useStations } from '@/hooks/useStations';
import { format2 } from '@/utils/charts';
import type { AGCStorageDetailRow } from '@/types/api';

const { RangePicker } = DatePicker;

import { DEFAULT_PENALTY_BASE, DEFAULT_EFF, DEFAULT_CURT_THRES } from '@/config/constants';

const RevenuePage: React.FC = () => {
  const printRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: stationsData, isLoading: stationsLoading } = useStations();
  const stations = stationsData?.stations ?? [];

  const [selectedDisplay, setSelectedDisplay] = useState('');
  const stationReal = selectedDisplay;

  useEffect(() => {
    if (stations.length > 0 && !selectedDisplay) {
      setSelectedDisplay(stations[0]);
    }
  }, [stations, selectedDisplay]);

  const today = dayjs();
  const yesterday = today.subtract(1, 'day');
  const firstDayOfMonth = today.startOf('month');
  const [dates, setDates] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([firstDayOfMonth, yesterday]);

  const penaltyBase = DEFAULT_PENALTY_BASE;
  const capMax = 0;
  const eff = DEFAULT_EFF;
  const curtThres = DEFAULT_CURT_THRES;

  const startDateStr = dates[0].format('YYYY-MM-DD');
  const endDateStr = dates[1].format('YYYY-MM-DD');
  const isSingleDay = startDateStr === endDateStr;

  const pvParams = useMemo(() => ({
    station: stationReal,
    start_date: startDateStr,
    end_date: endDateStr,
  }), [stationReal, startDateStr, endDateStr]);

  const storageParams = useMemo(() => ({
    station: stationReal,
    start_date: startDateStr,
    end_date: endDateStr,
    penalty_base: penaltyBase,
    cap_max: capMax,
    eff,
    curt_thres: curtThres,
  }), [stationReal, startDateStr, endDateStr, penaltyBase, capMax, eff, curtThres]);

  const {
    data: pvData,
    refetch: refetchPv,
  } = usePvRevenue(pvParams);

  const {
    data: storageData,
    refetch: refetchStorage,
  } = useStorageRevenue(storageParams);

  const {
    mutate: mutateAgc,
    data: agcData,
    isPending: agcLoading,
    error: agcError,
  } = useAgcStorage();

  // Multi-day AGC data for battery analysis chart (2-7 days)
  const [multiDayAgc, setMultiDayAgc] = useState<Record<string, AGCStorageDetailRow[]>>({});
  const [multiDayAgcLoading, setMultiDayAgcLoading] = useState(false);
  const multiDayRange = useMemo(() => {
    const s = dayjs(startDateStr);
    const e = dayjs(endDateStr);
    const days = e.diff(s, 'day') + 1;
    if (days >= 2 && days <= 7 && stationReal) {
      const dates: string[] = [];
      for (let i = 0; i < days; i++) {
        dates.push(s.add(i, 'day').format('YYYY-MM-DD'));
      }
      return dates;
    }
    return null;
  }, [startDateStr, endDateStr, stationReal]);

  const agcParams = useMemo(() => ({
    station: stationReal,
    date: startDateStr,
    penalty_base: penaltyBase,
    cap_max: capMax,
    eff,
    curt_thres: curtThres,
  }), [stationReal, startDateStr, penaltyBase, capMax, eff, curtThres]);

  // Auto-fetch multi-day AGC data for battery analysis chart
  useEffect(() => {
    if (!multiDayRange || multiDayRange.length === 0) {
      setMultiDayAgc({});
      return;
    }
    let cancelled = false;
    const fetchAll = async () => {
      setMultiDayAgcLoading(true);
      setMultiDayAgc({});
      const result: Record<string, AGCStorageDetailRow[]> = {};
      for (const d of multiDayRange) {
        if (cancelled) break;
        try {
          const res = await fetch(
            `/api/v1/agc-storage/data?station=${encodeURIComponent(stationReal)}&date=${d}&penalty_base=${penaltyBase}&cap_max=${capMax}&eff=${eff}&curt_thres=${curtThres}`,
            { credentials: 'include' }
          );
          const json = await res.json();
          const raw = json?.data?.rows ?? [];
          result[d] = raw.map((r: Record<string, unknown>) => ({
            time: String(r.time ?? ''),
            pv_avail: Number(r.pv_avail ?? 0),
            pv_act: Number(r.pv_act ?? 0),
            agc_target: Number(r.agc_target ?? 0),
            agc_act: Number(r.agc_act ?? 0),
            ust: Number(r.ust ?? 0),
            bat: Number(r.bat ?? 0),
            ep: Number(r.ep ?? 0),
            p: Number(r.p ?? 0),
            v: Number(r.v ?? 0),
            e: Number(r.e ?? 0),
            s: Number(r.s ?? 0),
            a: Number(r.a ?? 0),
            c_q: Number(r.c_q ?? 0),
            e_q: Number(r.e_q ?? 0),
            e_p: Number(r.e_p ?? 0),
            v_q: Number(r.v_q ?? 0),
            v_p: Number(r.v_p ?? 0),
            soc: Number(r.soc ?? 0),
          }));
        } catch {
          // skip failed days
        }
      }
      if (!cancelled) {
        setMultiDayAgc(result);
        setMultiDayAgcLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [multiDayRange?.join(','), stationReal, penaltyBase, capMax, eff, curtThres]);

  const handleQuery = () => {
    refetchPv();
    refetchStorage();
    setMultiDayAgc({});
    if (isSingleDay) {
      mutateAgc(agcParams);
    }
  };

  const handleExport = async () => {
    if (!printRef.current || !wrapperRef.current) return;
    if (pvRows.length === 0 && storageRows.length === 0) return;

    const wrapper = wrapperRef.current;
    const originalLeft = wrapper.style.left;
    const originalZIndex = wrapper.style.zIndex;
    wrapper.style.left = '0px';
    wrapper.style.zIndex = '99999';

    try {
      await new Promise((r) => setTimeout(r, 2000));
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, Math.min(imgHeight, 297));
      const s = startDateStr.replace(/-/g, '').slice(4);
      const e = endDateStr.replace(/-/g, '').slice(4);
      const titleDates = startDateStr === endDateStr ? s : `${s}-${e}`;
      pdf.save(`${selectedDisplay}场站${titleDates}收益测算.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      wrapper.style.left = originalLeft;
      wrapper.style.zIndex = originalZIndex;
    }
  };

  const pvRows = useMemo(() => {
    const raw = pvData?.data?.daily_rows ?? [];
    return raw.map((r: Record<string, unknown>) => ({
      '日期': String(r['日期'] ?? r.date ?? ''),
      '实时电费(元)': Number(r['实时电费(元)'] ?? r.real_time_revenue ?? 0),
      '场外补贴电费(元)': Number(r['场外补贴电费(元)'] ?? r.subsidy_revenue ?? 0),
      '省间现货电费(元)': Number(r['省间现货电费(元)'] ?? r.interprov_revenue ?? 0),
      '总收益(元)': Number(r['总收益(元)'] ?? r.total_revenue ?? 0),
      '总上网电量(MWh)': Number(r['总上网电量(MWh)'] ?? r.total_energy ?? 0),
    }));
  }, [pvData]);

  const storageRows = useMemo(() => {
    const raw = storageData?.data?.daily_rows ?? [];
    return raw.map((r: Record<string, unknown>) => ({
      '日期': String(r['日期'] ?? r.date ?? ''),
      '虚报挽回(元)': Number(r['虚报挽回(元)'] ?? r.v ?? 0),
      '现货净利(元)': Number(r['现货净利(元)'] ?? r.e ?? 0),
      '机制补贴(元)': Number(r['机制补贴(元)'] ?? r.s ?? 0),
      '规避考核(元)': Number(r['规避考核(元)'] ?? r.a ?? 0),
      '配储总收益(元)': Number(r['配储总收益(元)'] ?? r.total_revenue ?? 0),
      '总充电量(MWh)': Number(r['总充电量(MWh)'] ?? r.charge_energy ?? 0),
      '总放电量(MWh)': Number(r['总放电量(MWh)'] ?? r.discharge_energy ?? 0),
      '虚报挽回电量(MWh)': Number(r['虚报挽回电量(MWh)'] ?? r.v_energy ?? 0),
    }));
  }, [storageData]);

  const monthPdiffMap = useMemo(() => (pvData?.data?.month_pdiff_map as Record<string, number>) ?? {}, [pvData]);
  const monthMtdMap = useMemo(() => (pvData?.data?.month_mtd_map as Record<string, number>) ?? {}, [pvData]);
  const subsidyPrice = (pvData?.data?.subsidy_price as number) ?? 0;

  const pvMissing = useMemo(() => (pvData?.data?.missing_dates as string[]) ?? [], [pvData]);
  const storageMissing = useMemo(() => (storageData?.data?.missing_dates as string[]) ?? [], [storageData]);

  // 省间逐时段数据 + pdiff（供瀑布图按公式计算）
  const interprovPeriods = useMemo(() => {
    return (agcData?.data?.interprov_periods as Array<{period: string; power: number; price: number}>) ?? [];
  }, [agcData]);
  const agcPdiff = useMemo(() => (agcData?.data?.pdiff as number) ?? 0, [agcData]);

  const singleDayDetailRows = useMemo<AGCStorageDetailRow[]>(() => {
    if (!isSingleDay) return [];
    const raw = agcData?.data?.rows ?? [];
    return raw.map((r) => ({
      time: String(r.time ?? ''),
      pv_avail: Number(r.pv_avail ?? 0),
      pv_act: Number(r.pv_act ?? 0),
      agc_target: Number(r.agc_target ?? 0),
      agc_act: Number(r.agc_act ?? 0),
      ust: Number(r.ust ?? 0),
      bat: Number(r.bat ?? 0),
      ep: Number(r.ep ?? 0),
      p: Number(r.p ?? 0),
      v: Number(r.v ?? 0),
      e: Number(r.e ?? 0),
      s: Number(r.s ?? 0),
      a: Number(r.a ?? 0),
      c_q: Number(r.c_q ?? 0),
      e_q: Number(r.e_q ?? 0),
      e_p: Number(r.e_p ?? 0),
      v_q: Number(r.v_q ?? 0),
      v_p: Number(r.v_p ?? 0),
      soc: Number(r.soc ?? 0),
    }));
  }, [agcData, isSingleDay]);

  const pdiffEntries = Object.entries(monthPdiffMap);
  const hasZeroSubsidy = subsidyPrice === 0;

  const tabItems = [
    {
      key: 'summary',
      label: '汇总',
      children: (
        <RevenueSummaryTab
          pvRows={pvRows}
          storageRows={storageRows}
          pvMissing={pvMissing}
          storageMissing={storageMissing}
          monthPdiffMap={monthPdiffMap}
          monthMtdMap={monthMtdMap}
          isSingleDay={isSingleDay}
          singleDayDetailRows={singleDayDetailRows}
          singleDayLoading={agcLoading}
          singleDayError={agcError ?? null}
          onSingleDayRetry={() => mutateAgc(agcParams)}
          batteryChartData={multiDayAgc}
          interprovPeriods={interprovPeriods}
          agcPdiff={agcPdiff}
          batteryChartLoading={multiDayAgcLoading}
        />
      ),
    },
    {
      key: 'pv',
      label: '光伏',
      children: (
        <RevenuePvTab
          rows={pvRows}
          monthPdiffMap={monthPdiffMap}
          monthMtdMap={monthMtdMap}
          missingDates={pvMissing}
          subsidyPrice={subsidyPrice}
        />
      ),
    },
    {
      key: 'storage',
      label: '配储',
      children: (
        <RevenueStorageTab
          rows={storageRows}
          detailRows={singleDayDetailRows}
          isSingleDay={isSingleDay}
          displayStation={selectedDisplay}
          startDate={startDateStr}
          endDate={endDateStr}
          missingDates={storageMissing}
          detailLoading={agcLoading}
          detailError={agcError ?? null}
          onDetailRetry={() => mutateAgc(agcParams)}
        />
      ),
    },
    {
      key: 'capacity-fee',
      label: '容量电费',
      children: (
        <CapacityFeeTab
          station={selectedDisplay}
          month={startDateStr.slice(0, 7).replace('-', '')}
        />
      ),
    },
  ];

  if (stationsLoading) {
    return (
      <PageContainer title="场站收益测算">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Spin size="large" tip="正在加载场站列表..." />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="场站收益测算">
      {/* Control bar */}
      <div style={{ paddingBottom: 16, marginBottom: 24, borderBottom: '1px solid #e5e7eb' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>场站</div>
            <Select
              value={selectedDisplay}
              options={stations.map((d) => ({ value: d, label: d }))}
              onChange={setSelectedDisplay}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>日期范围</div>
            <RangePicker
              value={dates}
              onChange={(vals) => {
                if (vals && vals[0] && vals[1]) {
                  setDates([vals[0], vals[1]]);
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={5}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>&nbsp;</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} style={{ flex: 1 }}>
                查询
              </Button>
              <Button icon={<FilePdfOutlined />} onClick={handleExport} disabled={pvRows.length === 0 && storageRows.length === 0} style={{ flex: 1 }}>
                PDF
              </Button>
            </div>
          </Col>
        </Row>


        {/* Pdiff info */}
        {pdiffEntries.length === 1 && (
          <div style={{ marginTop: 16, padding: '12px 16px', background: '#fff7ed', border: '1px solid #ffedd5', borderRadius: 10, fontSize: 13, color: '#c2410c' }}>
            场站补贴电价基数: <strong>{format2(subsidyPrice)}</strong> 元/MWh ｜
            当月光伏MTD均价: <strong>{format2(Number(Object.values(monthMtdMap)[0]) || 0)}</strong> 元/MWh ｜
            推算补贴电价: <strong>{format2(pdiffEntries[0][1])}</strong> 元/MWh
          </div>
        )}
        {pdiffEntries.length > 1 && (
          <Row gutter={[16, 8]} style={{ marginTop: 12 }}>
            {pdiffEntries.map(([key, pdiff]) => (
              <Col key={key}>
                <Card size="small">
                  <Statistic title={key} value={format2(pdiff)} suffix="元/MWh" />
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    MTD: {format2(monthMtdMap[key] || 0)} 元/MWh
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        {hasZeroSubsidy && (
          <Alert
            message="该场站在 station_master 中未配置 subsidy_price，Pdiff 将按 0 计算补贴电价基数。"
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
          />
        )}
      </div>

      {/* Tabs */}
      <Tabs items={tabItems} defaultActiveKey="summary" />

      {/* 打印视图（移出视口） */}
      <div
        ref={wrapperRef}
        style={{ position: 'fixed', left: '-9999px', top: 0, zIndex: -1 }}
      >
        <div ref={printRef}>
          <RevenuePrintView
            pvRows={pvRows}
            storageRows={storageRows}
            singleDayDetailRows={singleDayDetailRows}
            isSingleDay={isSingleDay}
            displayStation={selectedDisplay}
            startDate={startDateStr}
            endDate={endDateStr}
            batteryChartData={multiDayAgc}
            interprovPeriods={interprovPeriods}
            agcPdiff={agcPdiff}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default RevenuePage;
